import { subDays, subMonths, subWeeks } from "date-fns";

import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { PostgrestError } from "@supabase/supabase-js";
import { logger } from "interfaces/logger";
import {
  addUserJobOverview,
  addPaginatedUserJobs,
  selectJobsByEventType,
  addJob,
  selectUserJobOverviewByEventType,
  increasePageNumber,
  selectJobsPagenumber,
  deleteJobInteraction as deleteJobInteractionAction,
  copyJobInteraction as copyJobInteractionAction,
  revertCopyJobInteraction,
} from "store/jobsSlice";
import store from "store/store";

import { useAccount } from "contexts/AccountContext";
import { useAuth } from "contexts/authContext";

import { supabase } from "apis/supabaseClient";

import { useRealtimeChannelHealth } from "utils/realtime";

import { COPILOT_PAGE_SIZE } from "constants/jobs";

export type JobInteractionEvent = "apply" | "view" | "save";

export type JobSearch = {
  jobId?: string;
  search?: {
    value?: string;
    filters?: {
      location?: string[] | string;
      remote?: JobModality[] | JobModality;
      tailored?: "forYou" | "all";
      datePosted?: "pastMonth" | "pastWeek" | "past24Hours" | "anytime";
    };
  };
};

export type Filter = keyof NonNullable<
  NonNullable<JobSearch["search"]>["filters"]
>;
export type FilterValue<T extends Filter> = NonNullable<
  NonNullable<JobSearch["search"]>["filters"]
>[T];

export type dbUserJob = {
  job_id: string;
  org_name?: string;
  org_photo_url?: string;
  title?: string;
  source?: "linkedin" | "indeed" | "lever" | "wellfound" | "ziprecruiter";
  location?: string;
  description?: string;
  requirements?: string;
  posting_date?: string;
  modality?: JobModality;
  event_type: JobInteractionEvent;
  url: string;
};

export type JobModality = "on-site" | "remote" | "hybrid";

export type Job = {
  jobId: string;
  orgName?: string;
  orgPhotoUrl?: string;
  title?: string;
  source?: "linkedin" | "indeed" | "lever" | "wellfound" | "ziprecruiter";
  location?: string;
  description?: string;
  requirements?: string;
  postingDate?: string;
  modality?: JobModality;
  eventType: JobInteractionEvent;
  url: string;
};

export type JobPreview = Omit<Job, "source" | "description" | "requirements">;

export type UserJobs = {
  viewed: Job[];
  saved: Job[];
  applied: Job[];
};

const formatUserJobsFromDb = (data: dbUserJob[] | dbTrendingJob[]): Job[] => {
  return data.map((dbJob) => ({
    jobId: dbJob.job_id,
    orgName: dbJob.org_name,
    orgPhotoUrl: dbJob.org_photo_url,
    title: dbJob.title,
    source: dbJob.source,
    location: dbJob.location,
    description: dbJob.description,
    requirements: dbJob.requirements,
    postingDate: dbJob.posting_date,
    modality: dbJob.modality,
    eventType: dbJob.event_type,
    url: dbJob.url,
  }));
};

export type UserJobOverview = {
  view: number;
  save: number;
  apply: number;
};

type dbUserJobOverview = {
  view: number;
  save: number;
  max_save: number;
  apply: number;
  max_apply: number;
};

const getJobOverview = async (): Promise<UserJobOverview> => {
  const { data, error } = await supabase.rpc("get_job_overview");

  if (error) {
    throw new Error(error.message);
  }

  const dbJobOverview: dbUserJobOverview = data[0].counts;

  const userJobOverview: UserJobOverview = {
    view: dbJobOverview.view,
    save: dbJobOverview.save,
    apply: dbJobOverview.apply,
  };

  store.dispatch(addUserJobOverview(userJobOverview));
  return userJobOverview;
};

export const useJobOverview = (): UserJobOverview & { isLoaded: boolean } => {
  const [isLoaded, setIsLoaded] = useState(false);
  const view = useSelector(selectUserJobOverviewByEventType("view"));
  const save = useSelector(selectUserJobOverviewByEventType("save"));
  const apply = useSelector(selectUserJobOverviewByEventType("apply"));

  const { user } = useAuth();
  useJobInteractionRealtime(user?.id);
  useEffect(() => {
    const get = async () => {
      await getJobOverview();
      setIsLoaded(true);
    };
    get();
  }, []);

  return { view, save, apply, isLoaded };
};

export const findJob = async (
  url: string,
  eventType: JobInteractionEvent,
  userId: string
): Promise<Job | null> => {
  const { data, error } = await supabase
    .from("v_job_interaction")
    .select("*")
    .eq("url", url)
    .eq("event_type", eventType)
    .eq("user_id", userId)
    .not("org_name", "is", null);

  if (error) {
    logger(error.message, "error");
    throw new Error(error.message);
  }
  if (data.length === 0) return null;
  return formatUserJobsFromDb([data[0] as dbUserJob])[0];
};

export const findAllSitemapJobs = async (): Promise<string[]> => {
  const { data } = await supabase
    .from("job")
    .select("id")
    .gt("expire_at", new Date().toISOString())
    .neq("org_name", null)
    .neq("org_name", "")
    .neq("title", null)
    .neq("title", "");
  if (!data || data?.length === 0) {
    return [];
  }

  return data.map((job) => job.id);
};

export const getJob = async (id: string): Promise<Job> => {
  const { data, error } = await supabase.rpc("get_job", {
    p_job_id: id,
  });

  if (error || !data.length) {
    throw new Error(error?.message || "No job found");
  }

  return formatUserJobsFromDb(data)[0];
};

export const getUserJobs = async ({
  pageNumber,
  pageSize,
  type,
}: {
  pageNumber: number;
  pageSize: number;
  type: JobInteractionEvent;
}): Promise<Job[]> => {
  const { data, error } = await supabase.rpc("get_jobs_paginated", {
    page_number: pageNumber,
    page_size: pageSize,
    query_job_interaction_event_type: type,
  });

  if (error) {
    throw new Error(error.message);
  }

  const jobs = formatUserJobsFromDb(data);
  store.dispatch(addPaginatedUserJobs({ jobInteraction: type, jobs }));
  return jobs;
};

export const useJobs = (
  eventType: JobInteractionEvent
): {
  isFinalPage: boolean;
  jobs: Job[];
  loadMoreJobs: () => void;
  isLoadingMore: boolean;
} => {
  const { user } = useAuth();
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const { accountProfile } = useAccount(true);
  const jobs = useSelector(selectJobsByEventType(eventType));
  const pageNumber = useSelector(selectJobsPagenumber(eventType));
  const totalJobsNumber = useSelector(
    selectUserJobOverviewByEventType(eventType)
  );

  useJobInteractionRealtime(user?.id);

  const loadMoreJobs = useCallback(async () => {
    setIsLoadingMore(true);
    await getUserJobs({
      pageNumber: pageNumber + 1,
      pageSize: COPILOT_PAGE_SIZE,
      type: eventType,
    });
    store.dispatch(increasePageNumber(eventType));
    setIsLoadingMore(false);
  }, [eventType, pageNumber]);

  useEffect(() => {
    if (!accountProfile.id || pageNumber > 0) return;
    loadMoreJobs();
  }, [accountProfile.id, eventType, loadMoreJobs, pageNumber]);

  return {
    isFinalPage: jobs.length >= totalJobsNumber,
    jobs,
    loadMoreJobs,
    isLoadingMore,
  };
};

export const useJobInteractionRealtime = (userId?: string) => {
  const [isLoaded, setIsLoaded] = useState(false);

  const jobInteractionChannelName = "db-job-interaction";
  const isChannelHealthy = useRealtimeChannelHealth(
    jobInteractionChannelName,
    !!userId
  );

  useEffect(() => {
    if (!userId || (isLoaded && isChannelHealthy)) return;

    const channel = supabase.channel(jobInteractionChannelName);

    channel.on(
      "postgres_changes",
      {
        event: "INSERT",
        schema: "public",
        table: "job_interaction",
        filter: `user_id=eq.${userId}`,
      },
      async (payload) => {
        // we may not find a job on INSERT because although the job_interaction was inserted, the job is still being parsed.
        // we only find job here if it was previously parsed before.
        const newJob = await findJob(
          payload.new.job_page_url,
          payload.new.event_type,
          payload.new.user_id
        );
        if (!newJob) return;
        store.dispatch(
          addJob({ job: newJob, jobInteraction: payload.new.event_type })
        );
      }
    );

    channel.on(
      "postgres_changes",
      {
        event: "UPDATE",
        schema: "public",
        table: "job_interaction",
        filter: `user_id=eq.${userId}`,
      },
      async (payload) => {
        // we are listening for updates. If the above INSERT failed to find a job, we wait for a job to be parsed.
        // When job is finished parsing, we update the job_page table and set the job_id column
        // We have a trigger for that table, when the job_id goes from null to something, we update this table.
        // No data/columns is changed on this trigger. This trigger is only there so we can trigger this Realtime.
        const newJob = await findJob(
          payload.new.job_page_url,
          payload.new.event_type,
          payload.new.user_id
        );
        if (!newJob) return;
        store.dispatch(
          addJob({ job: newJob, jobInteraction: payload.new.event_type })
        );
      }
    );

    channel.subscribe(async (status) => {
      if (status === "SUBSCRIBED" && !isLoaded) {
        setIsLoaded(true);
      }
    });
  }, [isLoaded, isChannelHealthy, userId]);
};

export type dbTrendingJob = dbUserJob & {
  view_count: number;
};

export const deleteJobInteraction = async (
  jobId: string,
  jobUrl: string,
  userId: string,
  eventType: JobInteractionEvent
): Promise<{ error: PostgrestError | null }> => {
  store.dispatch(
    deleteJobInteractionAction({ jobId, jobInteractionEvent: eventType })
  );
  const { error } = await supabase
    .from("job_interaction")
    .delete()
    .eq("user_id", userId)
    .eq("event_type", eventType)
    .eq("job_page_url", jobUrl);

  return { error };
};

export const copyJobInteraction = async (
  jobId: string,
  jobUrl: string,
  userId: string,
  originalEventType: JobInteractionEvent,
  newEventType: "save" | "apply"
): Promise<{ error: string | null }> => {
  store.dispatch(
    copyJobInteractionAction({
      jobId,
      originalJobInteractionEvent: originalEventType,
      newJobInteractionEvent: newEventType,
    })
  );
  const { error } = await supabase.from("job_interaction").insert({
    user_id: userId,
    job_page_url: jobUrl,
    event_type: newEventType,
  });

  if (error) {
    store.dispatch(
      revertCopyJobInteraction({
        jobId,
        newJobInteractionEvent: newEventType,
      })
    );
    if (error.code == "23505") {
      return {
        error: `This job is already marked as ${
          newEventType === "save" ? "saved" : "applied"
        }`,
      };
    }
    logger(`copyJobInteraction failed with error: ${error.message}`, "error");
    return {
      error: `Failed to mark job as ${
        newEventType === "save" ? "saved" : "applied"
      }`,
    };
  }
  return { error: null };
};

const searchJobs = async ({
  pageNumber,
  pageSize,
  searchValue,
  postingDateLimit,
  locations,
  modalities,
  isForUser = true,
}: {
  pageNumber: number;
  pageSize: number;
  searchValue?: string;
  postingDateLimit?: Date;
  locations?: string[];
  modalities?: JobModality[];
  isForUser?: boolean;
}): Promise<{
  jobs: JobPreview[];
  count: number;
}> => {
  const { data, error } = await supabase.rpc("search_jobs", {
    page_number: pageNumber,
    page_size: pageSize,
    search_value: searchValue,
    posting_date_limit: postingDateLimit?.toISOString(),
    p_locations: locations,
    p_modalities: modalities,
    for_user: isForUser,
  });

  if (error) {
    throw new Error(error.message);
  }

  return {
    jobs: formatUserJobsFromDb(data || []),
    count: data?.length ? data[0].total_count : 0,
  };
};

const searchJobsFromQueryParams = ({
  jobSearch,
  pageNumber,
  pageSize,
}: {
  jobSearch?: JobSearch;
  pageNumber: number;
  pageSize: number;
}) => {
  return searchJobs({
    pageNumber: pageNumber,
    pageSize: pageSize,
    searchValue: jobSearch?.search?.value,
    postingDateLimit: jobSearch?.search?.filters?.datePosted
      ? {
          pastMonth: subMonths(new Date(), 1),
          pastWeek: subWeeks(new Date(), 1),
          past24Hours: subDays(new Date(), 1),
          anytime: undefined,
        }[jobSearch?.search?.filters?.datePosted]
      : undefined,
    locations:
      typeof jobSearch?.search?.filters?.location === "string"
        ? [jobSearch.search.filters.location]
        : jobSearch?.search?.filters?.location,
    modalities:
      typeof jobSearch?.search?.filters?.remote === "string"
        ? [jobSearch.search.filters.remote]
        : jobSearch?.search?.filters?.remote,
    isForUser: jobSearch?.search?.filters?.tailored !== "all",
  });
};

export const useQuerySearchedJobs = (
  pageSize = 20
): {
  isFinalPage: boolean;
  jobs: JobPreview[];
  loadMoreJobs: () => void;
  isLoadingMore: boolean;
  count?: number;
  fetchJobs: (jobSearch: JobSearch) => void;
} => {
  const [isFinalPage, setIsFinalPage] = useState(false);
  const [pageNumber, setPageNumber] = useState(0);
  const [count, setCount] = useState<number>();
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [jobSearch, setJobSearch] = useState<JobSearch>();
  const [jobs, setJobs] = useState<JobPreview[]>([]);

  const loadMoreJobs = useCallback(async () => {
    if (isFinalPage) return;
    setIsLoadingMore(true);
    const data = await searchJobsFromQueryParams({
      pageNumber: pageNumber + 1,
      pageSize: pageSize,
      jobSearch,
    });
    setPageNumber((prev) => prev + 1);
    if (data.jobs.length + jobs.length >= data.count) setIsFinalPage(true);
    setJobs((prev) => [...prev, ...data.jobs]);
    setCount(data.count);
    setIsLoadingMore(false);
  }, [pageNumber, jobSearch, jobs.length, pageSize, isFinalPage]);

  useEffect(() => {
    if (!jobSearch || pageNumber > 0 || isLoadingMore) return;
    loadMoreJobs();
  }, [jobSearch, pageNumber, isLoadingMore, loadMoreJobs]);

  const fetchJobs = useCallback((search: JobSearch) => {
    // reset all and set query
    setIsFinalPage(false);
    setPageNumber(0);
    setCount(undefined);
    setIsLoadingMore(false);
    setJobs([]);
    setJobSearch(search);
  }, []);

  return {
    isFinalPage,
    jobs,
    loadMoreJobs,
    isLoadingMore,
    fetchJobs,
    count: count,
  };
};

export const insertJobEventType = async (
  jobUrl: string,
  userId: string,
  eventType: JobInteractionEvent
) => {
  const { error } = await supabase
    .from("job_interaction")
    .insert({
      job_page_url: jobUrl,
      user_id: userId,
      event_type: eventType,
    })
    .select("*")
    .single();
  if (error) {
    throw new Error(error.message);
  }
};

export const deleteJobEventType = async (
  jobUrl: string,
  userId: string,
  eventType: JobInteractionEvent
): Promise<{ error: PostgrestError | null }> => {
  const { error } = await supabase
    .from("job_interaction")
    .delete()
    .eq("user_id", userId)
    .eq("job_page_url", jobUrl)
    .eq("event_type", eventType);

  return { error };
};
